import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Popup from '../../ui/popup/Popup';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';

import { PlatformSelector } from './PlatformSelector';
import { TimeframeSelector } from './TimeframeSelector';
import TextArea from '../../ui/inputs/TextArea';
import MainPurpleButton from '../../ui/buttons/MainPurpleButton';
import SquareGrayButton from '../../ui/buttons/SquareGrayButton';
import '../../styles/components/prompt-builder/PromptBuilderModal.scss';
import { RootState } from '../../store/store';
import { CategorySelector } from './CategorySelector';
import { PROMPT_BUILDER_WEBHOOK_URL } from '../../lib/constants/central-api';

interface PromptBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsertPrompt?: (prompt: string) => void;
}

export const PromptBuilderModal: React.FC<PromptBuilderModalProps> = ({
  isOpen,
  onClose,
  onInsertPrompt,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [timeframe, setTimeframe] = useState<string>('Last 7 days');
  const [customDateRange, setCustomDateRange] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [additionalInput, setAdditionalInput] = useState<string>('');
  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);

  const totalSteps = 5;

  // Reset modal to step 1 when opened
  useEffect(() => {
    if (isOpen) {
      handleReset();
    }
  }, [isOpen]);

  // Auth credentials
  const getAuthHeader = () => {
    const username = atob('QWRVcA=='); // Base64 encoded "AdUp"
    const password = atob('TWFpa2VsMTIz'); // Base64 encoded "Maikel123"
    const token = btoa(`${username}:${password}`);
    return `Basic ${token}`;
  };

  const handleGenerate = async () => {
    if (!selectedGoal) {
      setError('Please select a goal');
      return;
    }

    if (selectedCategories.length === 0) {
      setError('Please select at least one category');
      return;
    }

    setIsLoading(true);
    setError(null);

    const categoryNames = selectedCategories.map((id) => {
      const categoryMap: Record<string, string> = {
        performance: 'Performance Overview',
        ad_performance: 'Ad Performance',
        conversions: 'Conversions & Sales',
        revenue: 'Revenue & Financial Overview',
        audience: 'Audience & Demographics',
        funnel: 'Sales Funnel & Checkout Journey',
        engagement: 'Engagement & Interactions',
        leads: 'Leads & Contacts Management',
        attribution: 'Attribution & Multi-Channel Impact',
      };
      return categoryMap[id] || id;
    });

    const platformNames = selectedPlatforms.map((id) => {
      const platformMap: Record<string, string> = {
        shopify: 'Shopify',
        stripe: 'Stripe',
        intercom: 'Intercom',
        hubspot: 'Hubspot',
        woocommerce: 'WooCommerce',
        google_analytics: 'Google Analytics',
        google_ads: 'Google Ads',
        google_search_console: 'Google Search Console',
        facebook_ads: 'Facebook Ads',
        tiktok_ads: 'TikTok Ads',
        linkedin_ads: 'LinkedIn Ads',
      };
      return platformMap[id] || id;
    });

    const generateSmartPrompt = () => {
      const goalPrefix =
        {
          Report: 'Generate a comprehensive report on',
          'Unique Insights': 'Provide unique insights and analysis on',
          Recommendations: 'Give actionable recommendations for improving',
        }[selectedGoal] || 'Analyze';

      const categoriesText = categoryNames.join(', ');
      const platformsText =
        platformNames.length > 0 ? ` from ${platformNames.join(', ')}` : '';
      const timeframeText =
        timeframe === 'Custom' ? customDateRange : timeframe.toLowerCase();
      const additionalText = additionalInput ? `. ${additionalInput}` : '';

      return `${goalPrefix} ${categoriesText}${platformsText} for the ${timeframeText}${additionalText}. Include key metrics, trends, and actionable insights.`;
    };

    const params = new URLSearchParams();
    params.append('goal', selectedGoal);
    params.append('categories', JSON.stringify(categoryNames));
    params.append('platforms', JSON.stringify(platformNames));
    params.append(
      'timeframe',
      timeframe === 'Custom' ? customDateRange : timeframe
    );
    if (additionalInput.trim()) {
      params.append('additionalInput', additionalInput.trim());
    }

    try {
      // Try with minimal headers to avoid CORS issues
      const response = await fetch(
        `${PROMPT_BUILDER_WEBHOOK_URL}?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json, text/plain, */*',
            Authorization: getAuthHeader(),
          },
          mode: 'cors',
          credentials: 'omit',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseText = await response.text();

      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from server');
      }

      let extractedPrompt = '';

      try {
        const jsonData = JSON.parse(responseText);

        if (
          jsonData &&
          typeof jsonData.prompt === 'string' &&
          jsonData.prompt.trim()
        ) {
          extractedPrompt = jsonData.prompt.trim();
        } else if (
          jsonData &&
          typeof jsonData.generated_prompt === 'string' &&
          jsonData.generated_prompt.trim()
        ) {
          extractedPrompt = jsonData.generated_prompt.trim();
        } else {
          console.log('No valid prompt found in JSON response');
        }
      } catch (jsonError) {
        console.log(
          'Response is not valid JSON, trying other methods:',
          jsonError
        );
      }

      // If JSON parsing failed, try regex patterns
      if (!extractedPrompt) {
        const promptPatterns = [
          /"prompt"\s*:\s*"([^"]+)"/,
          /"generated_prompt"\s*:\s*"([^"]+)"/,
          /prompt:\s*([^\n\r]+)/i,
          /generated_prompt:\s*([^\n\r]+)/i,
        ];

        for (const pattern of promptPatterns) {
          const match = responseText.match(pattern);
          if (match && match[1] && match[1].trim().length > 10) {
            extractedPrompt = match[1].trim();

            break;
          }
        }
      }

      // Final fallback
      if (
        !extractedPrompt &&
        responseText.trim().length > 20 &&
        !responseText.includes('<html')
      ) {
        const cleanText = responseText
          .replace(/^[^a-zA-Z]*/, '')
          .replace(/[^a-zA-Z.!?]*$/, '')
          .trim();

        if (cleanText.length > 20) {
          extractedPrompt = cleanText;
          console.log('Extracted prompt from clean text:', extractedPrompt);
        }
      }

      const finalPrompt = extractedPrompt || generateSmartPrompt();

      setGeneratedPrompt(finalPrompt);
      setCurrentStep(6);
    } catch (err) {
      console.warn('Webhook request failed, using smart fallback:', err);
      console.error('Error details:', err);

      // Handle different types of errors
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          console.log('Request was aborted (likely timeout or CORS issue)');
        } else if (err.message.includes('Failed to fetch')) {
          console.log('Network error - likely CORS or connectivity issue');
        } else {
          console.log('Other error:', err.message);
        }
      }

      // Always use fallback - this should work regardless of webhook issues
      const fallbackPrompt = generateSmartPrompt();
      setGeneratedPrompt(fallbackPrompt);
      setCurrentStep(6);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsertIntoChat = () => {
    if (onInsertPrompt && generatedPrompt) {
      console.log('Calling onInsertPrompt with:', generatedPrompt);
      onInsertPrompt(generatedPrompt);
    } else {
      console.log('onInsertPrompt not available or no generated prompt');
    }
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setCurrentStep(1);
    setSelectedGoal('');
    setSelectedCategories([]);
    setSelectedPlatforms([]);
    setTimeframe('Last 7 days');
    setCustomDateRange('');
    setGeneratedPrompt('');
    setError(null);
    setAdditionalInput('');
    setIsLoading(false); // Reset loading state
  };

  const handleClose = () => {
    console.log('Modal closing - resetting to step 1');
    handleReset();
    onClose();
  };

  const handleNext = () => {
    setError(null);

    if (currentStep === 1 && !selectedGoal) {
      setError('Please select a goal to continue');
      return;
    }

    if (currentStep === 2 && selectedCategories.length === 0) {
      setError('Please select at least one category to continue');
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedGoal !== '';
      case 2:
        return selectedCategories.length > 0;
      case 3:
        return true; // Platforms are optional
      case 4:
        return true; // Timeframe always has a default value
      case 5:
        return true; // Additional input is optional
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className='prompt-builder-modal__step-content'>
            <div className='prompt-builder-modal__step-header'>
              <h2>Select Your Goal</h2>
              <p>Choose what type of output you want to generate</p>
            </div>

            <div className='prompt-builder-modal__goal-options'>
              {['Report', 'Unique Insights', 'Recommendations'].map((goal) => (
                <div
                  key={goal}
                  className={`prompt-builder-modal__goal-option ${
                    selectedGoal === goal ? 'selected' : ''
                  }`}
                  style={
                    isDarkMode
                      ? {
                          backgroundColor:
                            selectedGoal === goal ? '#f59e0b' : '#374151',
                          borderColor:
                            selectedGoal === goal ? '#f59e0b' : '#4b5563',
                          color: selectedGoal === goal ? '#000' : '#e5e7eb',
                        }
                      : undefined
                  }
                  onClick={() => {
                    setSelectedGoal(goal);
                    setError(null);
                    setCurrentStep(2); // Automatically advance to step 2
                  }}
                >
                  <div
                    className={`prompt-builder-modal__goal-radio ${
                      selectedGoal === goal ? 'selected' : ''
                    }`}
                    style={
                      isDarkMode
                        ? {
                            backgroundColor:
                              selectedGoal === goal ? '#f59e0b' : '#374151',
                            borderColor:
                              selectedGoal === goal ? '#f59e0b' : '#6b7280',
                          }
                        : undefined
                    }
                  >
                    {selectedGoal === goal && (
                      <div
                        className='prompt-builder-modal__goal-radio-dot'
                        style={
                          isDarkMode ? { backgroundColor: '#000' } : undefined
                        }
                      />
                    )}
                  </div>
                  <span
                    className='prompt-builder-modal__goal-label'
                    style={
                      isDarkMode
                        ? { color: selectedGoal === goal ? '#000' : '#e5e7eb' }
                        : undefined
                    }
                  >
                    {goal}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className='prompt-builder-modal__step-content'>
            <div className='prompt-builder-modal__step-header'>
              <h2>Select Business Categories</h2>
              <p>Choose one or more categories to include in your prompt</p>
            </div>

            <CategorySelector
              selectedCategories={selectedCategories}
              onChange={setSelectedCategories}
            />
          </div>
        );

      case 3:
        return (
          <div className='prompt-builder-modal__step-content'>
            <div className='prompt-builder-modal__step-header'>
              <h2>Select Platforms</h2>
              <p>
                Choose the platforms you want to include data from (optional)
              </p>
            </div>

            <PlatformSelector
              selectedPlatforms={selectedPlatforms}
              onChange={setSelectedPlatforms}
            />
          </div>
        );

      case 4:
        return (
          <div className='prompt-builder-modal__step-content'>
            <div className='prompt-builder-modal__step-header'>
              <h2>Select Timeframe</h2>
              <p>Choose the time period for your data</p>
            </div>

            <div className='prompt-builder-modal__timeframe-wrapper'>
              <TimeframeSelector
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
                customDateRange={customDateRange}
                onCustomDateRangeChange={setCustomDateRange}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className='prompt-builder-modal__step-content'>
            <div className='prompt-builder-modal__step-header'>
              <h2>Additional Context</h2>
              <p>
                Add any specific requirements or context for your prompt
                (optional)
              </p>
            </div>

            <div className='prompt-builder-modal__additional-input-wrapper'>
              <TextArea
                innerPlaceholder='E.g., Compare with previous period, focus on mobile users, exclude canceled orders...'
                value={additionalInput}
                changeListeners={[(e) => setAdditionalInput(e.target.value)]}
                id='additional-input'
                classes='prompt-builder-modal__textarea'
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className='prompt-builder-modal__step-content'>
            <div className='prompt-builder-modal__step-header'>
              <h2>Generated Prompt</h2>
              <p>Your prompt has been successfully generated</p>
            </div>

            <div className='prompt-builder-modal__generated-content'>
              <TextArea
                value={generatedPrompt}
                readonly
                id='generated-prompt'
                classes='prompt-builder-modal__textarea'
              />

              <div className='prompt-builder-modal__generated-badges'>
                {selectedGoal && (
                  <span className='prompt-builder-modal__badge goal'>
                    {selectedGoal}
                  </span>
                )}
                {selectedCategories.map((category) => (
                  <span
                    key={`cat-${category}`}
                    className='prompt-builder-modal__badge category'
                  >
                    {category}
                  </span>
                ))}
                {selectedPlatforms.map((platform) => (
                  <span
                    key={`plat-${platform}`}
                    className='prompt-builder-modal__badge platform'
                  >
                    {platform}
                  </span>
                ))}
                <span className='prompt-builder-modal__badge timeframe'>
                  {timeframe === 'Custom' ? customDateRange : timeframe}
                </span>
              </div>

              <div className='prompt-builder-modal__generated-actions'>
                <SquareGrayButton
                  title='Create New'
                  value='Create New'
                  onClick={handleReset}
                  classes='prompt-builder-modal__button'
                />
                <MainPurpleButton
                  value='Insert into Chat'
                  onClick={handleInsertIntoChat}
                  classes='prompt-builder-modal__button'
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Popup
      styles={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      setTrigger={handleClose}
      trigger={isOpen}
      isCloseBtnHidden={false}
    >
      <div className='prompt-builder-modal'>
        {isLoading && (
          <div
            className='prompt-builder-modal__loading-overlay'
            style={{
              background: isDarkMode
                ? 'rgba(17, 24, 39, 0.98)'
                : 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <div className='prompt-builder-modal__loading-content'>
              <LoadingSpinner />
              <p
                style={{
                  color: isDarkMode ? '#e5e7eb' : '#374151',
                }}
              >
                Generating your prompt...
              </p>
            </div>
          </div>
        )}

        {currentStep <= totalSteps && (
          <div className='prompt-builder-modal__progress'>
            <div className='prompt-builder-modal__progress-text'>
              Step {currentStep} of {totalSteps}
            </div>
            <div className='prompt-builder-modal__progress-bar'>
              <div
                className='prompt-builder-modal__progress-fill'
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {renderStep()}

        {error && (
          <div className='prompt-builder-modal__error'>
            <p>{error}</p>
          </div>
        )}

        {currentStep <= totalSteps && (
          <div className='prompt-builder-modal__navigation'>
            {currentStep === 1 ? (
              <MainPurpleButton
                value='Cancel'
                onClick={handleClose}
                classes='prompt-builder-modal__nav-button prompt-builder-modal__nav-button--secondary'
              />
            ) : (
              <MainPurpleButton
                value='Back'
                onClick={handleBack}
                classes='prompt-builder-modal__nav-button prompt-builder-modal__nav-button--secondary'
              />
            )}

            {currentStep === totalSteps ? (
              <MainPurpleButton
                value={isLoading ? 'Generating...' : 'Generate Prompt'}
                onClick={() => {
                  handleGenerate();
                }}
                disabled={isLoading || !canProceed()}
                classes='prompt-builder-modal__nav-button'
              />
            ) : (
              <MainPurpleButton
                value='Next'
                onClick={handleNext}
                disabled={!canProceed()}
                classes='prompt-builder-modal__nav-button'
              />
            )}
          </div>
        )}
      </div>
    </Popup>
  );
};
